import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Grid,
  Stack,
  Button,
  useTheme,
  alpha,
  Chip,
  TablePagination,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import {
  Visibility,
  TrendingUp,
  BusinessCenter,
  Schedule,
  AccountBalance,
  ArrowForward,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAllStartups } from '../store/slices/adminSlice';
import { getFileLink } from '../utils/getFileLink';
import dayjs from 'dayjs';

const STATUS_CONFIG = {
  PENDING: {
    label: 'Beklemede',
    color: 'default',
  },
  APPROVED: {
    label: 'Onaylandı',
    color: 'success',
  },
  REJECTED: {
    label: 'Reddedildi',
    color: 'error',
  },
  WAITING_REVIEW: {
    label: 'İnceleme Bekliyor',
    color: 'warning',
  },
  WAITING_INVESTMENT_COMMITTEE: {
    label: 'Yatırım Komitesi İncelemesinde',
    color: 'info',
  },
  WAITING_MKK_APPROVAL: {
    label: 'KFS Onayı Bekliyor',
    color: 'secondary',
  },
  WAITING_TAKASBANK_APPROVAL: {
    label: 'Takasbank Onayı Bekliyor',
    color: 'primary',
  },
  TAKASBANK_APPROVED: {
    label: 'Takasbank Onaylandı',
    color: 'success',
  }
};

const AllStartupsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const { allStartups, loading } = useSelector((state) => state.admin);

  useEffect(() => {
    const filters = {
      page: page + 1,
      limit: rowsPerPage,
    };

    if (startDate && endDate) {
      filters.dateRange = `${new Date(startDate).getTime()}-${new Date(endDate).getTime()}`;
    }

    if (selectedStatus.length > 0) {
      filters.status = selectedStatus;
    }

    dispatch(fetchAllStartups(filters));
  }, [dispatch, page, rowsPerPage, startDate, endDate, selectedStatus]);

  useEffect(() => {
    console.log('Current allStartups:', allStartups);
  }, [allStartups]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setSelectedStatus(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedStatus([]);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Belirtilmemiş';
    return dayjs(Number(timestamp)).format('DD MMM YYYY');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'WAITING_REVIEW':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'Onaylandı';
      case 'PENDING':
        return 'Beklemede';
      case 'REJECTED':
        return 'Reddedildi';
      case 'WAITING_REVIEW':
        return 'İnceleme Bekliyor';
      default:
        return status || 'Belirsiz';
    }
  };

  if (loading) return <LinearProgress />;

  const startups = allStartups?.list || [];
  const totalCount = allStartups?.count || 0;

  const renderFilters = () => (
    <Card sx={{ mb: 3, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Filtreler</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<Clear />}
            onClick={handleClearFilters}
            variant="outlined"
            size="small"
          >
            Temizle
          </Button>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterList />
          </IconButton>
        </Stack>
      </Stack>

      {showFilters && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Başlangıç Tarihi"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Bitiş Tarihi"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                multiple
                value={selectedStatus}
                onChange={handleStatusChange}
                input={<OutlinedInput label="Durum" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={STATUS_CONFIG[value]?.label}
                        size="small"
                        color={STATUS_CONFIG[value]?.color}
                      />
                    ))}
                  </Box>
                )}
              >
                {Object.entries(STATUS_CONFIG).map(([value, config]) => (
                  <MenuItem key={value} value={value}>
                    <Chip
                      label={config.label}
                      size="small"
                      color={config.color}
                      sx={{ mr: 1 }}
                    />
                    {config.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
    </Card>
  );

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" gutterBottom>
                Tüm Projeler
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Platform üzerindeki tüm projeleri görüntüleyin, detaylı inceleme yapın ve durumlarını takip edin.
                {totalCount > 0 && ` Toplam ${totalCount} proje bulunmaktadır.`}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<TrendingUp />}
                  sx={{
                    bgcolor: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: theme.palette.success.dark,
                    },
                  }}
                >
                  İstatistikler
                </Button>
                <Button
                  variant="contained"
                  startIcon={<BusinessCenter />}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                  }}
                >
                  Yeni Proje
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {renderFilters()}

        {/* Projects Grid */}
        <Grid container spacing={3}>
          {startups.map((startup) => (
            <Grid item xs={12} key={startup.startupId}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: theme.customShadows?.z8,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.customShadows?.z16,
                  },
                }}
              >
                <Grid container spacing={3} alignItems="center">
                  <Grid item>
                    <Avatar
                      src={startup.startupProfile?.mainLogoPath ? getFileLink(startup.startupProfile.mainLogoPath) : ''}
                      alt={startup.startupProfile?.name || 'Logo'}
                      sx={{ 
                        width: 64, 
                        height: 64,
                        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      }}
                    />
                  </Grid>
                  <Grid item xs>
                    <Stack spacing={1}>
                      <Typography variant="h6">
                        {startup.startupProfile?.name || 'İsim Belirtilmemiş'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {startup.startupProfile?.shortDescription || 'Açıklama bulunmamaktadır.'}
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={STATUS_CONFIG[startup.status]?.label || getStatusLabel(startup.status)}
                          color={STATUS_CONFIG[startup.status]?.color || getStatusColor(startup.status)}
                          size="small"
                          sx={{ borderRadius: 1 }}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            Kayıt: {formatDate(startup.registerTime)}
                          </Typography>
                        </Stack>
                        {startup.mkkProjectId && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <BusinessCenter sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              MKK ID: {startup.mkkProjectId}
                            </Typography>
                          </Stack>
                        )}
                        {startup.takasBankIban && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AccountBalance sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              IBAN: {startup.takasBankIban}
                            </Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  </Grid>
                  <Grid item>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate(`/startuplar/detay/${startup.startupId}`)}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.2s',
                        }}
                      >
                        İncele
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
          
          {startups.length === 0 && (
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.background.default, 0.7),
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Henüz proje bulunmamaktadır.
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Pagination */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına proje:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
            }
          />
        </Box>
      </Box>
    </Container>
  );
};

export default AllStartupsPage; 